import {
  CaretDownOutlined,
  DeleteOutlined,
  GlobalOutlined,
  MailOutlined,
  MobileOutlined,
  QuestionCircleOutlined,
  QuestionOutlined,
} from '@ant-design/icons';
import { Button } from '@mm/components';
import { AngleDownLightIcon } from '@mm/icons';
import SuppressionSettingsController from '@mm/state/controllers/SuppressionSettingsController';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { ReactComponent as ManualSuppressionIcon } from 'assets/customize.svg';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  errorToast,
  successToast,
} from '../../../../../../../../common/helpers/toast';
import { isUrl } from '../../../../../../../components/SlideoutMenu/menus/SlideoutEmail/editor/utils';
import { colors } from 'packages/utils';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import css from './ManualIntegrations.module.css';
import { landmineNames } from '@mm/constants/landmine-names';
import {
  CaptureActionEnum,
  SuppressionSettingsModel,
} from '@mm/state/model/SuppressionSettingsModel';
import { ContactSuppressionHeader } from '../ContactSuppressions/ContactSuppressionHeader';
import { Collapse, CollapseProps, Input } from 'antd';
import { HelpCenter } from '../../../../../../../../common/components/HelpCenter/HelpCenter';

export const ManualIntegrations = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const [settings, setSettings] = useState<SuppressionSettingsModel[]>([]);
  const [inputs, setInputs] = useState<{ [key in CaptureActionEnum]: string }>({
    [CaptureActionEnum.do_not_send]: '',
    [CaptureActionEnum.do_not_capture]: '',
  });
  const [viewFull, setViewFull] = useState<{
    [key in CaptureActionEnum]: boolean;
  }>({
    [CaptureActionEnum.do_not_send]: false,
    [CaptureActionEnum.do_not_capture]: false,
  });
  const [busyCustom, setBusyCustom] = useState(false);
  const [customListSource, setCustomListSource] = useState<{
    [key in CaptureActionEnum]: any[];
  }>({
    [CaptureActionEnum.do_not_send]: [],
    [CaptureActionEnum.do_not_capture]: [],
  });

  const supressionSetting = settings.find(
    s => s.captureAction === CaptureActionEnum.do_not_send,
  );
  const unsubscribeSettings = settings.find(
    s => s.captureAction === CaptureActionEnum.do_not_capture,
  );

  const { openNewConfirmModal, closeModal } = useModalActions();
  const CAP = 3;

  const sectionProps = {
    landmineName: landmineNames.INTEGRATIONS_CONTACT_SUPPRESSION,
    featureFlag: 'contactSuppression',
  };

  const customListView = (captureAction: CaptureActionEnum) =>
    viewFull[captureAction]
      ? customListSource[captureAction]
      : customListSource[captureAction].slice(0, CAP);

  const onFullListClick = (captureAction: CaptureActionEnum) => {
    setViewFull(prev => ({
      ...prev,
      [captureAction]: true,
    }));
  };

  const fetchSettings = async () => {
    const res = await SuppressionSettingsController.getSettings(facebookPageId);
    setSettings(res);

    const requiredCaptureActions = [
      CaptureActionEnum.do_not_send,
      CaptureActionEnum.do_not_capture,
    ];

    const sendSignalSetting = res.find(s => s.data?.sendSignal);

    const updatedSettings = await Promise.all(
      requiredCaptureActions.map(async action => {
        let setting = res.find(s => s.captureAction === action);
        if (!setting?.id) {
          setting = await SuppressionSettingsController.createSettings(
            facebookPageId,
            { captureAction: action },
          );

          if (sendSignalSetting?.data) {
            setting = await SuppressionSettingsController.updateSettings(
              facebookPageId,
              setting.id,
              { data: sendSignalSetting.data },
            );
          }
        }
        return setting;
      }),
    );

    const listSources: { [key in CaptureActionEnum]: any[] } = {
      [CaptureActionEnum.do_not_send]: [],
      [CaptureActionEnum.do_not_capture]: [],
    };

    updatedSettings.forEach(setting => {
      listSources[setting.captureAction] = setting.list || [];
    });

    setCustomListSource(listSources);
  };

  const handleDelete = async (
    id: string | number,
    captureAction: CaptureActionEnum,
  ) => {
    setBusyCustom(true);
    const setting =
      captureAction === CaptureActionEnum.do_not_send
        ? supressionSetting
        : unsubscribeSettings;

    try {
      if (setting?.id) {
        const list = customListSource[captureAction].filter(
          item => item !== id,
        );

        await SuppressionSettingsController.updateSettings(
          facebookPageId,
          setting.id,
          {
            ...setting,
            listToDelete: [...(setting.listToDelete || []), id],
          },
        );

        setCustomListSource(prev => ({
          ...prev,
          [captureAction]: list,
        }));

        successToast(`Removed: "${id}"`);
      }
    } catch (err) {
      errorToast(`Error removing: "${id}"`, 'top-right');
    } finally {
      setBusyCustom(false);
    }
  };

  const handleCustomDeleteClick = (
    value: string | number,
    captureAction: CaptureActionEnum,
  ) => {
    openNewConfirmModal({
      title: `Manually add Emails/Domains`,
      subtitle: `Are you sure you want to remove "${value}"?`,
      onResolve: async () => {
        await handleDelete(value, captureAction);
        closeModal();
      },
      onReject: closeModal,
    });
  };

  const handleAddCustomSuppressionClick = async (
    value: string,
    captureAction: CaptureActionEnum,
  ) => {
    setBusyCustom(true);
    const setting =
      captureAction === CaptureActionEnum.do_not_send
        ? supressionSetting
        : unsubscribeSettings;

    try {
      if (setting?.id) {
        const list = Array.from(
          new Set([value, ...customListSource[captureAction]]),
        );

        await SuppressionSettingsController.updateSettings(
          facebookPageId,
          setting.id,
          {
            ...setting,
            list,
            captureAction,
          },
        );

        setCustomListSource(prev => ({
          ...prev,
          [captureAction]: list,
        }));

        setInputs(prev => ({
          ...prev,
          [captureAction]: '',
        }));

        successToast(`Added: "${value}"`);
      }
    } catch (err) {
      errorToast(`Error adding: "${value}"`, 'top-right');
    } finally {
      setBusyCustom(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const renderContent = (captureAction: CaptureActionEnum) => (
    <div className={css.content}>
      <div className={css.inputField}>
        <Input
          type="text"
          placeholder={`Enter Domains, Emails, Phone Numbers to ${
            captureAction === CaptureActionEnum.do_not_send
              ? 'suppress'
              : 'unsubscribe'
          }`}
          value={inputs[captureAction]}
          onChange={e =>
            setInputs(prev => ({
              ...prev,
              [captureAction]: e?.target?.value.trim(),
            }))
          }
          className={css.input}
          variant="outlined"
          onPressEnter={() =>
            handleAddCustomSuppressionClick(
              inputs[captureAction],
              captureAction,
            )
          }
        />
        <Button
          onClick={() =>
            handleAddCustomSuppressionClick(
              inputs[captureAction],
              captureAction,
            )
          }
          disabled={!inputs[captureAction] || busyCustom}
          css={{ container: css.primaryButton }}
        >
          Add
        </Button>
      </div>

      <div className={css.inputList}>
        {customListView(captureAction)?.map(item => {
          const icon =
            typeof item === 'string' && item.includes('@') ? (
              <MailOutlined />
            ) : typeof item === 'string' && item.startsWith('+') ? (
              <MobileOutlined />
            ) : typeof item === 'string' && isUrl(item) ? (
              <GlobalOutlined />
            ) : null;

          return (
            <div className={css.listItem} key={item}>
              <span className={css.inputListTitle}>
                {icon}
                {item}
              </span>
              <span className={css.inputListActions}>
                <div className={css.inputListAction}>
                  <div
                    className={css.listAction}
                    onClick={
                      busyCustom
                        ? undefined
                        : () => handleCustomDeleteClick(item, captureAction)
                    }
                  >
                    <DeleteOutlined />
                  </div>
                </div>
              </span>
            </div>
          );
        })}

        {customListSource[captureAction].length > CAP &&
          !viewFull[captureAction] && (
            <div className={css.inputListItem}>
              <span
                className={classNames(css.inputListTitle, css.clickable)}
                onClick={() => onFullListClick(captureAction)}
              >
                View full list <AngleDownLightIcon color={colors.gray9} />
              </span>
            </div>
          )}
      </div>
    </div>
  );

  const items: CollapseProps['items'] = [
    {
      key: CaptureActionEnum.do_not_send,
      label: <ContactSuppressionHeader isSuppression />,
      children: renderContent(CaptureActionEnum.do_not_send),
    },
    {
      key: CaptureActionEnum.do_not_capture,
      label: <ContactSuppressionHeader isSuppression={false} />,
      children: renderContent(CaptureActionEnum.do_not_capture),
    },
  ];

  return (
    <div className={css.container}>
      <BaseIntegration
        openOnMount
        titleComponent={
          <BaseIntegrationHeader
            title="Manually add Emails/Domains"
            subtitle="Choose emails, domains, and phone numbers that your automations will not engage with.
            We automatically suppress bounces, unsubscribes and other emails we believe to be problematic."
            icon={<ManualSuppressionIcon />}
            headerRightComponent={
              <HelpCenter
                title=""
                helpLink="https://customers.ai/help/l/en/article/uhl81gxos2-enable-email-suppression-list"
                icon={<QuestionCircleOutlined style={{ fontSize: 20 }} />}
              />
            }
            css={{ icon: 'integrations__header-icon-manual-suppression' }}
            {...sectionProps}
          />
        }
      >
        <div className={css.contentWrapper}>
          <div className={css.label}>Sources</div>
          <Collapse
            items={items}
            defaultActiveKey={[
              CaptureActionEnum.do_not_send,
              CaptureActionEnum.do_not_capture,
            ]}
            className={classNames('integrations__collapse', css.collapse)}
            expandIcon={({ isActive }) => (
              <CaretDownOutlined
                style={{ fontSize: 20 }}
                rotate={isActive ? 180 : 0}
              />
            )}
          />
        </div>
      </BaseIntegration>
    </div>
  );
};
